import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getBroadcastGroups, createBroadcast, cancelBroadcastGroup } from '@/services/api';
import { getBroadcastGroupDetails, updateBroadcastGroup } from '@/services/api/broadcast';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import BroadcastHeader from './components/BroadcastHeader';
import BroadcastStats from './components/BroadcastStats';
import BroadcastFiltersComponent from './components/BroadcastFilters';
import BroadcastGroupGrid from './components/BroadcastGroupGrid';
import { ViewBroadcastGroupModal } from './components/ViewBroadcastGroupModal';
import { BroadcastFilters, BroadcastFormData } from './types/broadcast.types';
import {
    sortGroupBroadcasts,
    filterGroupBroadcasts,
    getGroupStatus,
    getGroupTotalSent,
    getGroupSuccessRate,
} from './utils/groupBroadcastUtils';
import { getEsteira } from '@/services/api/utility-esteiras';
import { Loader2, MessageSquare, ListChecks, CalendarDays } from 'lucide-react';
import BroadcastCalendar from './components/BroadcastCalendar';
import CreateBroadcastModal from './components/CreateBroadcastModal';
import type { BroadcastGroupSummary } from '@/types/broadcast';
import FiltroUsuarioAdmin, { useFiltroUsuarioAdmin } from '@/components/FiltroUsuarioAdmin';

type PageOption = { id: string | number; name: string; page_id: string }

export default function Broadcasts() {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isViewGroupModalOpen, setIsViewGroupModalOpen] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
    const [aba, setAba] = useState<'execucoes' | 'calendario'>('execucoes');
    const [page, setPage] = useState(1);
    const perPage = 10;
    const [filters, setFilters] = useState<BroadcastFilters>({
        sortBy: 'created_at',
        sortOrder: 'desc'
    });

    // O recorte de data muda o universo, nao so a ordem: sem voltar pra
    // primeira pagina o usuario fica numa pagina que o novo filtro nao tem
    // mais, e a tela responde vazia com resultado existindo.
    const handleFiltersChange = (novos: BroadcastFilters) => {
        const mudouData = novos.dateFrom !== filters.dateFrom || novos.dateTo !== filters.dateTo;
        setFilters(novos);
        if (mudouData) setPage(1);
    };

    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Admin vê a operação de todos os usuários e filtra por dono no servidor;
    // vale para as duas abas, por isso fica fora do painel de filtros.
    const { usuarioId } = useFiltroUsuarioAdmin();
    const filtroUsuario = usuarioId ?? undefined;

    // Query para listar broadcasts a nível de grupo (paginado)
    const { data: paginatedData, isLoading, error, isFetching, refetch } = useQuery({
        queryKey: ['broadcast-groups', page, perPage, filters.dateFrom, filters.dateTo, filtroUsuario ?? null],
        queryFn: () => getBroadcastGroups(page, perPage, filters.dateFrom, filters.dateTo, filtroUsuario),
        staleTime: 1000 * 30,
        refetchInterval: 1000 * 60,
        refetchOnWindowFocus: false,
    });

    const groups = useMemo((): BroadcastGroupSummary[] => paginatedData?.items ?? [], [paginatedData]);
    const totalPages = paginatedData?.pages ?? 1;
    const totalGroups = paginatedData?.total ?? 0;

    const pages = useMemo<PageOption[]>(() => {
        if (!groups?.length) return [];
        const unique = new Map<string, PageOption>();
        for (const g of groups) {
            for (const p of g.pages ?? []) {
                const id = String(p.page_id ?? '').trim();
                const name = String(p.page_name ?? '').trim();
                if (!id || !name) continue;
                if (!unique.has(id)) unique.set(id, { id: p.id ?? id, name, page_id: id });
            }
        }
        return Array.from(unique.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    }, [groups]);

    const availableLanguages = useMemo(() => {
        const langs = new Set<string>();
        for (const g of groups) {
            if (g.ai_translate && g.target_language) langs.add(g.target_language);
        }
        return Array.from(langs).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }, [groups]);

    const categories = useMemo(() => [] as { id: number; name: string }[], []);

    const filteredAndSortedGroups = useMemo(() => {
        let result = filterGroupBroadcasts(groups, filters);
        const sortBy: 'created_at' | 'total_sent' | 'success_rate' =
            filters.sortBy === 'sent_count' ? 'total_sent' :
                filters.sortBy === 'success_rate' ? 'success_rate' : 'created_at';
        result = sortGroupBroadcasts(result, sortBy, filters.sortOrder || 'desc');
        return result;
    }, [groups, filters]);

    const stats = useMemo(() => {
        const totalSent = groups.reduce((sum, g) => sum + getGroupTotalSent(g), 0);
        const finishedGroups = groups.filter(g => {
            const s = getGroupStatus(g);
            return s === 'sent' || s === 'partial';
        });
        const averageSuccessRate = finishedGroups.length > 0
            ? finishedGroups.reduce((sum, g) => sum + getGroupSuccessRate(g), 0) / finishedGroups.length
            : 0;
        const pendingCount = groups.filter(g => ['pending', 'processing'].includes(getGroupStatus(g))).length;

        return {
            totalBroadcasts: totalGroups,
            totalSent,
            averageSuccessRate,
            pendingCount
        };
    }, [groups, totalGroups]);

    const temFiltroAtivo = Boolean(
        filters.status || filters.page || filters.category ||
        filters.targetLanguage || (filters.dateFrom && filters.dateTo)
    );

    const periodoAtivo = useMemo(() => {
        if (!filters.dateFrom || !filters.dateTo) return '';
        const br = (d: string) => d.split('-').reverse().join('/');
        return `${br(filters.dateFrom)} e ${br(filters.dateTo)}`;
    }, [filters.dateFrom, filters.dateTo]);

    const limparFiltros = () => {
        handleFiltersChange({ sortBy: filters.sortBy, sortOrder: filters.sortOrder });
    };

    const [duplicateInitialData, setDuplicateInitialData] = useState<BroadcastFormData | undefined>(undefined);
    const [groupToCancel, setGroupToCancel] = useState<BroadcastGroupSummary | null>(null);
    // Horário original guardado para saber se o usuário mexeu: sem mudança o
    // backend recupera o horário no fuso de cada página, que o front não conhece.
    const [editingGroup, setEditingGroup] = useState<{ id: number; scheduledTime: string } | null>(null);

    const handleCreateClick = () => {
        setEditingGroup(null);
        setDuplicateInitialData(undefined);
        setIsCreateModalOpen(true);
    };

    // TODO: rota desabilitada temporariamente
    // const handleViewGroup = (group: BroadcastGroupSummary) => {
    //     setSelectedGroupId(group.group_broadcast_id);
    //     setIsViewGroupModalOpen(true);
    // };

    const prefillFromGroup = (group: BroadcastGroupSummary): BroadcastFormData => {
        const uniquePages = (group.pages ?? []).map(p => ({
            id: p.id,
            page_id: p.page_id,
            page_name: p.page_name,
        }));
        return {
            title: group.title || group.name || undefined,
            selectedPages: uniquePages,
            blocks: group.template_params?.blocks ?? [],
            isScheduled: false,
            scheduledTime: '',
            broadcastType: group.utility_template_id ? 'utility' : (group.message_tag ? 'message_tag' : 'standard'),
            messageTag: group.message_tag ?? null,
            utilityTemplateId: group.utility_template_id ?? null,
            secondaryUtilityTemplateId: group.secondary_utility_template_id ?? null,
            aiTranslate: !!group.ai_translate,
            sourceLanguage: group.source_language,
        };
    };

    const handleDuplicate = (group: BroadcastGroupSummary) => {
        const prefill = prefillFromGroup(group);
        setEditingGroup(null);
        setDuplicateInitialData({ ...prefill, title: prefill.title ? `Cópia de ${prefill.title}` : undefined });
        setIsCreateModalOpen(true);
    };

    const handleEdit = (group: BroadcastGroupSummary) => {
        // datetime-local quer "AAAA-MM-DDTHH:mm" no horário de parede de BRT
        const scheduledTime = new Intl.DateTimeFormat('sv-SE', {
            timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: false,
        }).format(new Date(group.scheduled_at as string)).replace(' ', 'T');
        setEditingGroup({ id: group.group_broadcast_id, scheduledTime });
        setDuplicateInitialData({ ...prefillFromGroup(group), isScheduled: true, scheduledTime });
        setIsCreateModalOpen(true);
    };

    /**
     * Duplicar a partir do calendario.
     *
     * O calendario carrega os grupos em modo slim — sem `template_params` nem
     * `pages`, que sao o que fazia um mes pesar 20,4 MB. Quem duplica precisa
     * dos dois, entao busca o detalhe do grupo no clique: 1 requisicao quando
     * alguem duplica, em vez de 325 payloads completos toda vez que alguem
     * abre o calendario.
     */
    const handleDuplicateFromCalendar = async (group: BroadcastGroupSummary) => {
        try {
            const completo = await getBroadcastGroupDetails(group.group_broadcast_id);
            // No detalhe, utility_template_id é o template de UMA página; o
            // duplicar precisa do id do grupo, que vem em *_group_id.
            handleDuplicate({
                ...group,
                ...completo,
                utility_template_id: completo.utility_template_group_id ?? group.utility_template_id,
                secondary_utility_template_id:
                    completo.secondary_utility_template_group_id ?? group.secondary_utility_template_id,
            } as BroadcastGroupSummary);
        } catch {
            toast({
                variant: 'destructive',
                title: 'Não consegui carregar o broadcast',
                description: 'Abra pela aba Execuções para duplicar.',
            });
        }
    };

    const handleCreateBroadcast = async (data: BroadcastFormData) => {
        const isUtility = data.broadcastType === 'utility';
        const isEsteira = data.broadcastType === 'esteira';
        const useEsteira = isEsteira || (isUtility && data.selectedEsteiraId != null);
        const pageIds = data.selectedPages.map(p => String(p.page_id)).filter(Boolean);

        // Para broadcasts normais exige página selecionada
        // Para utility (1+1) o backend resolve as páginas pelo group_id do template
        // Para a Esteira salva, a resolução final já vem persistida — é usada como snapshot
        if (!isUtility && !isEsteira && pageIds.length === 0) {
            toast({ variant: 'destructive', title: 'Erro', description: 'Selecione pelo menos uma página.' });
            return;
        }
        if (isUtility && !useEsteira && !data.utilityTemplateId) {
            toast({ variant: 'destructive', title: 'Erro', description: 'Selecione um template utility.' });
            return;
        }
        if (isEsteira && data.selectedEsteiraId == null) {
            toast({ variant: 'destructive', title: 'Erro', description: 'Selecione uma Esteira.' });
            return;
        }

        // Revisão v3 (PRD-esteira-multi-template-utility.md): o auxiliar de texto agora
        // vive por entrada de template_resolution (secondary_group_id), não mais como
        // um único campo de nível superior da Esteira — a resolução é repassada como
        // snapshot tal como veio, cada entrada já carregando seu próprio auxiliar.
        let utilityTemplateResolution: { group_id: number; page_ids: string[]; secondary_group_id?: number | null }[] | undefined;
        if (useEsteira) {
            try {
                const esteira = await getEsteira(data.selectedEsteiraId as number);
                utilityTemplateResolution = esteira.template_resolution;
            } catch {
                toast({ variant: 'destructive', title: 'Erro', description: 'Não foi possível carregar a Esteira selecionada.' });
                return;
            }
        }

        const payload = {
            ...(isUtility || isEsteira ? {} : { page_ids: pageIds }),
            title: data.title || undefined,
            blocks: data.blocks,
            is_scheduled: data.isScheduled,
            scheduled_at: data.isScheduled ? data.scheduledTime : undefined,
            message_tag: data.messageTag ?? undefined,
            ai_translate: data.aiTranslate,
            source_language: data.sourceLanguage || undefined,
            ai_provider: data.aiProvider,
            utility_template_id: useEsteira ? undefined : (data.utilityTemplateId ?? undefined),
            secondary_utility_template_id: useEsteira ? undefined : (data.secondaryUtilityTemplateId ?? undefined),
            utility_template_resolution: utilityTemplateResolution,
        };

        if (editingGroup) {
            const horarioMantido = data.isScheduled && data.scheduledTime === editingGroup.scheduledTime;
            updateGroupMutation.mutate({
                groupId: editingGroup.id,
                data: { ...payload, scheduled_at: horarioMantido ? undefined : payload.scheduled_at },
            });
            return;
        }
        createBroadcastMutation.mutate(payload);
    };

    const updateGroupMutation = useMutation({
        mutationFn: ({ groupId, data }: { groupId: number; data: Parameters<typeof updateBroadcastGroup>[1] }) =>
            updateBroadcastGroup(groupId, data),
        onSuccess: (_response, variables) => {
            toast({
                title: 'Broadcast atualizado!',
                description: variables.data.ai_translate
                    ? 'As traduções do conteúdo novo estão sendo geradas em background.'
                    : 'As alterações valem para o envio agendado.',
            });
            queryClient.invalidateQueries({ queryKey: ['broadcast-groups'] });
            setIsCreateModalOpen(false);
            setEditingGroup(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao editar broadcast',
                description: error.response?.data?.detail || error.response?.data?.message || 'Ocorreu um erro.',
            });
        },
    });

    const cancelGroupMutation = useMutation({
        mutationFn: (groupId: number) => cancelBroadcastGroup(groupId),
        onSuccess: (data) => {
            const cancelledCount = data.cancelled.length;
            const sendsCancelled = data.total_sends_cancelled;
            const skipped = data.skipped.length;
            toast({
                title: 'Grupo cancelado',
                description: `${cancelledCount} broadcast(s) cancelado(s), ${sendsCancelled.toLocaleString('pt-BR')} envios bloqueados${skipped > 0 ? ` (${skipped} já finalizado(s))` : ''}.`,
            });
            queryClient.invalidateQueries({ queryKey: ['broadcast-groups'] });
            setGroupToCancel(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao cancelar grupo',
                description: error.response?.data?.detail || error.response?.data?.message || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation para criar broadcast
    const createBroadcastMutation = useMutation({
        mutationFn: (data: Parameters<typeof createBroadcast>[0]) => createBroadcast(data),
        onSuccess: (response, variables) => {
            const description = variables.ai_translate
                ? 'Broadcast criado! A tradução automática está sendo gerada em background.'
                : 'O broadcast foi criado com sucesso.';
            toast({ title: 'Broadcast criado!', description });

            // Aviso não-bloqueante: broadcast já foi criado, só avisa quais
            // páginas ficarão sem o template complementar (recebem só o
            // principal).
            const warning = response.secondary_template_warning;
            if (warning && warning.missing_count > 0) {
                const names = warning.missing_pages
                    .map((p) => p.page_name || p.page_id)
                    .slice(0, 5)
                    .join(', ');
                const extra = warning.missing_count > 5 ? ` e mais ${warning.missing_count - 5}` : '';
                toast({
                    variant: 'default',
                    title: `${warning.missing_count} página(s) sem o template complementar aprovado`,
                    description: `${names}${extra} — vão receber só o template principal.`,
                });
            }

            setPage(1);
            queryClient.invalidateQueries({ queryKey: ['broadcast-groups'] });
            setIsCreateModalOpen(false);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar broadcast',
                description: error.response?.data?.detail || error.response?.data?.message || 'Ocorreu um erro.',
            });
        },
    });

    return (
        <div className="space-y-6">
            <BroadcastHeader
                onCreateClick={handleCreateClick}
                onRefresh={() => {
                    queryClient.invalidateQueries({ queryKey: ['broadcast-groups'] });
                    refetch();
                }}
                isRefreshing={isFetching}
            />

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex w-fit items-center gap-1 rounded-xl border border-gray-100 bg-white p-1">
                    {([
                        { id: 'execucoes' as const, rotulo: 'Execuções', icone: ListChecks },
                        { id: 'calendario' as const, rotulo: 'Calendário', icone: CalendarDays },
                    ]).map(({ id, rotulo, icone: Icone }) => (
                        <button
                            key={id}
                            type="button"
                            onClick={() => setAba(id)}
                            className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${aba === id
                                ? 'bg-[#0f172a] text-white'
                                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                        >
                            <Icone className="h-4 w-4" />
                            {rotulo}
                        </button>
                    ))}
                </div>

                <FiltroUsuarioAdmin className="w-full sm:w-64" onChange={() => setPage(1)} />
            </div>

            {aba === 'calendario' && (
                <BroadcastCalendar
                    onView={(group) => { setSelectedGroupId(group.group_broadcast_id); setIsViewGroupModalOpen(true); }}
                    onDuplicate={handleDuplicateFromCalendar}
                    onCancel={setGroupToCancel}
                    userId={filtroUsuario}
                />
            )}

            {aba === 'execucoes' && isLoading && (
                <div className="rounded-2xl bg-white p-8 shadow-sm border border-gray-100 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-[#0f172a]" />
                    <p className="ml-3 text-gray-500">Carregando broadcasts...</p>
                </div>
            )}

            {aba === 'execucoes' && error && (
                <div className="rounded-2xl bg-red-50 border border-red-200/50 p-6">
                    <p className="text-red-700 font-medium">Erro ao carregar broadcasts. Tente novamente.</p>
                </div>
            )}

            {aba === 'execucoes' && !isLoading && !error && groups.length > 0 && (
                <BroadcastStats
                    totalBroadcasts={stats.totalBroadcasts}
                    totalSent={stats.totalSent}
                    averageSuccessRate={stats.averageSuccessRate}
                    pendingCount={stats.pendingCount}
                />
            )}

            {aba === 'execucoes' && !error && (
                <BroadcastFiltersComponent
                    pages={pages}
                    categories={categories}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    availableLanguages={availableLanguages}
                />
            )}

            {aba === 'execucoes' && !isLoading && !error && filteredAndSortedGroups.length > 0 ? (
                <BroadcastGroupGrid
                    groups={filteredAndSortedGroups}
                    onView={(group) => { setSelectedGroupId(group.group_broadcast_id); setIsViewGroupModalOpen(true); }}
                    onDuplicate={handleDuplicate}
                    onCancel={setGroupToCancel}
                    onEdit={handleEdit}
                />
            ) : aba === 'execucoes' && !isLoading && !error ? (
                <div className="rounded-2xl bg-white p-12 shadow-sm border border-gray-100 text-center">
                    <div className="mx-auto w-16 h-16 bg-gradient-to-br from-[#7dd3fc]/20 to-[#38bdf8]/10 rounded-2xl flex items-center justify-center mb-4">
                        <MessageSquare className="w-8 h-8 text-[#0f172a]" />
                    </div>
                    {temFiltroAtivo ? (
                        <>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum broadcast neste filtro</h3>
                            <p className="text-gray-600 mb-6">
                                {periodoAtivo
                                    ? `Não há broadcast criado entre ${periodoAtivo}.`
                                    : 'Nenhum broadcast corresponde aos filtros ativos.'}
                            </p>
                            <Button variant="outline" className="rounded-xl" onClick={limparFiltros}>
                                Limpar filtros
                            </Button>
                        </>
                    ) : (
                        <>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum broadcast cadastrado</h3>
                            <p className="text-gray-600 mb-6">Crie seu primeiro broadcast para começar.</p>
                        </>
                    )}
                </div>
            ) : null}

            {/* Paginação */}
            {aba === 'execucoes' && !isLoading && !error && totalPages > 1 && (
                <div className="flex items-center justify-center gap-3">
                    <Button variant="outline" size="sm" className="rounded-xl" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                    <span className="text-sm text-gray-400">{page} de {totalPages}</span>
                    <Button variant="outline" size="sm" className="rounded-xl" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Próxima</Button>
                </div>
            )}

            <CreateBroadcastModal
                open={isCreateModalOpen}
                onOpenChange={setIsCreateModalOpen}
                onSubmit={handleCreateBroadcast}
                isLoading={createBroadcastMutation.isPending || updateGroupMutation.isPending}
                initialData={duplicateInitialData}
                isEditMode={!!editingGroup}
            />

            <ConfirmDialog
                open={!!groupToCancel}
                onOpenChange={(open) => { if (!open) setGroupToCancel(null); }}
                title="Cancelar grupo de broadcast?"
                description={
                    groupToCancel
                        ? `Todos os broadcasts pendentes do grupo "${groupToCancel.title || groupToCancel.name || `#${groupToCancel.group_broadcast_id}`}" serão cancelados. Broadcasts já enviados não serão afetados.`
                        : undefined
                }
                confirmLabel={cancelGroupMutation.isPending ? 'Cancelando...' : 'Cancelar grupo'}
                cancelLabel="Voltar"
                variant="danger"
                isLoading={cancelGroupMutation.isPending}
                onConfirm={() => groupToCancel && cancelGroupMutation.mutate(groupToCancel.group_broadcast_id)}
            />
            <ViewBroadcastGroupModal
                open={isViewGroupModalOpen}
                onOpenChange={setIsViewGroupModalOpen}
                groupBroadcastId={selectedGroupId}
            />
        </div>
    );
}
